import React, { useState } from 'react'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, Tooltip, useTheme } from '@mui/material'
import { type GridRenderCellParams, type GridValidRowModel, type GridTreeNodeWithRender } from '@mui/x-data-grid'
import { Actions, TableModalAction, type ExtraRowAction, type Row } from '../../types'
import { red, shade } from '../../../../colors'
import GenericButton from '../../../GenericButton'
import { ButtonSize } from '../../../GenericButton/styles'
import CallMadeIcon from '@mui/icons-material/CallMade'
interface ActionsWithDropdownProps {
  params: GridRenderCellParams<GridValidRowModel, any, any, GridTreeNodeWithRender>
  handleSelectedRow: () => void
  rowActions: Actions[]
  setActionModal: (arg: TableModalAction) => void
  onEditClick?: (row: Row) => void
  onPreviewClick?: (row: Row) => void
  editAction?: (row: Row) => void
  onOptOutClick?: (row: Row) => void
  onOptOutInstructorClick?: (row: Row) => void
  // Row actions to render disabled (greyed) with an explanatory tooltip, regardless of presence in rowActions.
  disabledRowActions?: Actions[]
  disabledRowActionTooltip?: string
  // Suppress the inline "Edit" button for tables with no edit modal (e.g. Activity Monitor).
  hideRowEditAction?: boolean
  // Feature-supplied menu items, each rendered only when the row carries its action.
  extraRowActions?: ExtraRowAction[]
}

const ActionsWithDropdown: React.FC<ActionsWithDropdownProps> = ({
  params,
  handleSelectedRow,
  rowActions,
  setActionModal,
  onEditClick,
  onPreviewClick,
  editAction,
  onOptOutClick,
  onOptOutInstructorClick,
  disabledRowActions,
  disabledRowActionTooltip,
  hideRowEditAction = false,
  extraRowActions
}) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = (): void => {
    setAnchorEl(null)
  }

  const handleAction = (action: TableModalAction): void => {
    handleSelectedRow()
    setActionModal(action)
    handleMenuClose()
  }

  const handleEdit = (): void => {
    onEditClick ? onEditClick(params?.row) : handleAction(TableModalAction.EDIT)
  }

  const handleDelete = (): void => {
    handleAction(TableModalAction.DELETE)
  }

  const handleArchive = (): void => {
    handleAction(TableModalAction.ARCHIVE)
  }
  const handlePreview = (): void => {
    if (params?.row) {
      onPreviewClick?.(params.row)
    } else {
      console.error('Error: params.row is undefined. Cannot proceed with Preview action.')
    }
  }

  const handleGoLIVE = (): void => {
    handleAction(TableModalAction.GO_LIVE)
  }

  const handleOptOut = (): void => {
    if (params?.row) {
      onOptOutClick?.(params.row)
    } else {
      console.error('Error: params.row is undefined. Cannot proceed with Opt Out action.')
    }
  }

  const handleOptOutInstructor = (): void => {
    if (params?.row) {
      onOptOutInstructorClick?.(params.row)
    } else {
      console.error('Error: params.row is undefined. Cannot proceed with Opt Out Instructors action.')
    }
  }

  const showEditOption = !hideRowEditAction && (Boolean(editAction) || rowActions?.includes(Actions.EDIT) || rowActions?.includes(Actions.READ))
  const showDeleteOption = rowActions?.includes(Actions.DELETE)
  const showArchiveOption = rowActions?.includes(Actions.ARCHIVE)
  const showPreviewOption = rowActions?.includes(Actions.SURVEY_PREVIEW)
  const showGoLive = rowActions?.includes(Actions.GO_LIVE)
  const showOptOut = rowActions?.includes(Actions.OPT_OUT)
  const isOptOutDisabled = !params?.row?.data?.isOptOutable
  const optOutTooltipTitle = isOptOutDisabled
    ? 'Opt-in/opt-out is not available for this course section.'
    : undefined

  const showOptOutInstructor = rowActions?.includes(Actions.OPT_OUT_INSTRUCTOR)

  const showReopenSurvey = rowActions?.includes(Actions.REOPEN_SURVEY)
  const showCloseSurvey = rowActions?.includes(Actions.CLOSE_SURVEY)
  const showUpdateTemplateRevision = rowActions?.includes(Actions.UPDATE_TEMPLATE_REVISION)
  const isUpdateTemplateRevisionDisabled = disabledRowActions?.includes(Actions.UPDATE_TEMPLATE_REVISION) ?? false

  const handleReopenSurvey = (): void => {
    handleAction(TableModalAction.REOPEN_SURVEY)
  }

  const handleCloseSurvey = (): void => {
    handleAction(TableModalAction.CLOSE_SURVEY)
  }

  const handleUpdateTemplateRevision = (): void => {
    handleAction(TableModalAction.UPDATE_TEMPLATE_REVISION)
  }

  // Only the items whose action the backend actually sent for this row.
  const visibleExtraRowActions = (extraRowActions ?? []).filter(
    ({ action }) => rowActions?.includes(action)
  )

  const isReadOnly = rowActions?.includes(Actions.READ) && visibleExtraRowActions.length === 0 && !showDeleteOption && !showArchiveOption && !showGoLive && !showReopenSurvey && !showOptOut && !showOptOutInstructor && !showCloseSurvey && !showUpdateTemplateRevision
  const showActionDropdown = showDeleteOption || showArchiveOption || showPreviewOption || showGoLive || showReopenSurvey || showOptOut || showOptOutInstructor || showCloseSurvey || showUpdateTemplateRevision || visibleExtraRowActions.length > 0

  return (
    <Box display='flex' justifyContent='center' alignItems='center' gap={1} >
      {showEditOption && (
        <GenericButton
          label='Edit'
          buttonSize={ButtonSize.Small}
          textTransform='capitalize'
          onClick={handleEdit}
          fontWeight='bold'

          style={{
            minWidth: theme.spacing(7),
            minHeight: theme.spacing(5),
            padding: '0',
            background: 'transparent'
          }}
        />
      )}
      <IconButton
        aria-label="more"
        aria-controls="action-dropdown"
        aria-haspopup="true"
        onClick={handleMenuClick}
        onMouseDown={(event) => { event.stopPropagation() }}
        disabled={isReadOnly}
        sx={{ visibility: (showActionDropdown || isReadOnly) ? 'visible' : 'hidden' }}
      >
        <MoreVertIcon />
      </IconButton>
      {
        showActionDropdown &&
        <Menu
          id="action-dropdown"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          elevation={4}
          sx={{
            '& .MuiMenu-paper': {
              borderRadius: theme.spacing(2),
              width: theme.spacing(38),
              marginTop: theme.spacing(2),
              boxShadow: '0px 4px 6px -2px rgba(32, 41, 57, 0.03), 0px 12px 16px -4px rgba(32, 41, 57, 0.08)',
              border: `1px solid ${shade[650]}`
            }
          }}
        >

          {showPreviewOption && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500' }} onClick={handlePreview}>
              Preview Survey<CallMadeIcon sx={{ width: '22px', height: '20px' }} />
            </MenuItem>
          )}
          {showGoLive && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500' }} onClick={handleGoLIVE}>
              Go Live Now
            </MenuItem>
          )}
          {showReopenSurvey && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500' }} onClick={handleReopenSurvey}>
              Reopen Survey
            </MenuItem>
          )}
          {showUpdateTemplateRevision && (
            <Tooltip
              title={isUpdateTemplateRevisionDisabled ? disabledRowActionTooltip : undefined}
              placement="left"
            >
              <span>
                <MenuItem
                  // The menu paper is a fixed, narrow width, so let the longest label
                  // ("Update Template Revision") wrap onto multiple lines, left-aligned,
                  // instead of being clipped by MenuItem's default white-space: nowrap.
                  sx={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'normal' }}
                  onClick={handleUpdateTemplateRevision}
                  disabled={isUpdateTemplateRevisionDisabled}
                >
                  Update Template Revision
                </MenuItem>
              </span>
            </Tooltip>
          )}
          {showArchiveOption && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500' }} onClick={handleArchive}>
              Archive
            </MenuItem>
          )}
          {showOptOut && (
            <Tooltip
              title={optOutTooltipTitle}
              placement="left"
            >
              <span>
                <MenuItem
                  sx={{ fontSize: '14px', fontWeight: '500' }}
                  onClick={handleOptOut}
                  disabled={isOptOutDisabled}
                >
                  {params?.row?.data?.isOptedOut ? 'Opt In' : 'Opt Out'}
                </MenuItem>
              </span>
            </Tooltip>
          )}
          {showOptOutInstructor && (
            <MenuItem
              // Match the Update Template Revision item: let long labels wrap onto multiple
              // lines, left-aligned, instead of being clipped by MenuItem's default nowrap.
              sx={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'normal' }}
              onClick={handleOptOutInstructor}
            >
              Opt Out Instructors
            </MenuItem>
          )}
          {showCloseSurvey && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500', color: red[700] }} onClick={handleCloseSurvey}>
              Close Survey
            </MenuItem>
          )}
          {showDeleteOption && (
            <MenuItem sx={{ fontSize: '14px', fontWeight: '500', color: red[700] }} onClick={handleDelete}>
              Delete
            </MenuItem>

          )}
          {visibleExtraRowActions.map(({ action, label, onClick, destructive }) => (
            <MenuItem
              key={action}
              sx={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'normal', color: destructive === true ? red[700] : undefined }}
              onClick={() => { onClick(params?.row); handleMenuClose() }}
            >
              {label}
            </MenuItem>
          ))}
        </Menu>
      }
    </Box>
  )
}

export default ActionsWithDropdown
